"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.resolvers = exports.ContextManager = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _value = require("./value");
var _util = require("@blockwell/util");
var _ContextReferenceResolver = require("./ContextReferenceResolver");
var _CurrentTimeVariable = require("./value/variable/type/CurrentTimeVariable");
var _eventer = require("@blockwell/eventer");
const resolvers = exports.resolvers = {};
resolvers["const"] = new _value.ConstantResolver();
resolvers["var"] = new _value.VariableResolver();
class ContextManager {
  get batches() {
    var _this$apis;
    return (_this$apis = this.apis) === null || _this$apis === void 0 ? void 0 : _this$apis.batches;
  }
  get abis() {
    var _this$apis2;
    return (_this$apis2 = this.apis) === null || _this$apis2 === void 0 ? void 0 : _this$apis2.abis;
  }
  get nft() {
    var _this$apis3;
    return (_this$apis3 = this.apis) === null || _this$apis3 === void 0 ? void 0 : _this$apis3.nft;
  }
  get client() {
    var _this$apis4, _this$apis4$batches;
    return (_this$apis4 = this.apis) === null || _this$apis4 === void 0 ? void 0 : (_this$apis4$batches = _this$apis4.batches) === null || _this$apis4$batches === void 0 ? void 0 : _this$apis4$batches.client;
  }

  /**
   * Shows if the context is attached to a context tree.
   * @protected
   */

  constructor(ownerKey, name) {
    (0, _defineProperty2.default)(this, "_parent", null);
    (0, _defineProperty2.default)(this, "children", []);
    (0, _defineProperty2.default)(this, "variables", []);
    (0, _defineProperty2.default)(this, "names", {});
    (0, _defineProperty2.default)(this, "collectionItems", {});
    (0, _defineProperty2.default)(this, "hasCollectionItems", false);
    (0, _defineProperty2.default)(this, "collections", {});
    (0, _defineProperty2.default)(this, "hasCollections", false);
    (0, _defineProperty2.default)(this, "events", new _eventer.Eventer());
    /**
     * Various APIs used by variables.
     *
     * All Contexts in the hierarchy have a reference to the same APIs.
     * @protected
     */
    (0, _defineProperty2.default)(this, "apis", void 0);
    (0, _defineProperty2.default)(this, "attached", void 0);
    /**
     * True if the context is active and variables need to be reactive.
     * @protected
     */
    (0, _defineProperty2.default)(this, "isActive", void 0);
    (0, _defineProperty2.default)(this, "resolver", new _ContextReferenceResolver.ContextReferenceResolver(this));
    /**
     * True if this context is in a repeat group, and not the first one. This is used in the
     * editor's tree view.
     */
    (0, _defineProperty2.default)(this, "repeated", void 0);
    (0, _defineProperty2.default)(this, "layoutEvents", false);
    (0, _defineProperty2.default)(this, "emitLayout", (0, _util.debounce)(() => {
      // Debounced to collect multiple changes into a single event

      this.events.emit("layout", {
        children: this.children.map(context => {
          return Object.freeze({
            context
          });
        }),
        variables: [...this.variables]
      });
    }, 0));
    this.ownerKey = ownerKey;
    this.name = name;
  }
  get parent() {
    return this._parent;
  }
  set parent(parent) {
    this._parent = parent;
    if (parent) {
      this.apis = parent.apis;
      this.attached = true;
      if (parent.active) {
        this.active = true;
      }
    } else {
      this.apis = undefined;
      this.attached = false;
    }
  }
  get active() {
    return this.isActive;
  }
  set active(active) {
    this.isActive = active;
  }
  attachAsRoot(batches, nft, abis, convertTypes, paletteParameters) {
    if (this.parent) {
      throw new Error("Can't attach Context as root, it already has a parent");
    }
    if (!this.name) {
      this.name = "root";
    }
    this.apis = {
      batches,
      abis,
      nft,
      paletteParameters,
      convertTypes
    };
    this.attached = true;
  }
  activate() {
    if (this._parent) {
      throw new Error("Can only activate from root");
    }
    this.active = true;
    let time = new _CurrentTimeVariable.CurrentTimeVariable();
    let ref = new _value.VariableReference(time, {}, true);
    this.addVariable(0, "currentTime", ref);
    time.startUpdates();
    let layer = [...this.children];
    while (layer.length > 0) {
      let nextLayer = [];
      for (let child of layer) {
        child.active = true;
        nextLayer = nextLayer.concat(child.children);
      }
      layer = nextLayer;
    }
  }
  onLayout(listener) {
    if (!this.layoutEvents) {
      this.layoutEvents = true;
      this.emitLayout();
    }
    this.events.on("layout", listener);
  }
  offLayout(listener) {
    this.events.off("layout", listener);
  }
  addChild(child) {
    if (child.parent) {
      child.parent.removeChild(child);
    }
    child.parent = this;
    this.children.push(child);
    if (this.layoutEvents) {
      this.emitLayout();
      child.emitLayout();
    }
  }
  removeChild(child) {
    this.children.splice(this.children.indexOf(child), 1);
    child.parent = null;
    if (this.layoutEvents) {
      this.emitLayout();
    }
  }
  remove() {
    var _this$parent;
    (_this$parent = this.parent) === null || _this$parent === void 0 ? void 0 : _this$parent.removeChild(this);
    return this;
  }
  stop(destroy = false) {
    let variables = this.variables;
    this.variables = [];
    this.names = {};
    this.collections = {};
    let collectionNames = Object.keys(this.collectionItems);
    this.collectionItems = {};
    this.hasCollections = false;
    this.hasCollectionItems = false;

    // Remove lookups from this context first, and then trigger the variables so anything
    // depending on these will get recalculated
    for (let variable of variables) {
      if (!destroy && variable.ref instanceof _value.VariableReference) {
        variable.ref.trigger();
      }
      variable.ref.stop();
    }
    if (!destroy) {
      if (collectionNames.length > 0) {
        for (let it of collectionNames) {
          this.triggerCollections(it);
        }
      }
    }
    this.attached = false;
    return this;
  }
  removeVariables(ownerKey) {
    var _this$parent2;
    let [vars, others] = (0, _util.partition)(this.variables, it => it.owner === ownerKey);
    this.variables = others;
    for (let it of vars) {
      let ref = it.ref;
      this.removeVariableLookups(it.name, it.ref);
      if (ref instanceof _value.VariableReference || ref instanceof _value.NullReference) {
        ref.trigger();
      }
      ref.stop();
    }
    (_this$parent2 = this.parent) === null || _this$parent2 === void 0 ? void 0 : _this$parent2.removeVariables(ownerKey);
  }
  removeVariableLookups(name, ref) {
    if (name) {
      let index = this.names[name].findIndex(it => it.ref === ref);
      if (index > -1) {
        this.names[name].splice(index, 1);
        if (Object.keys(this.names[name]).length === 0) {
          delete this.names[name];
        }
      }
      if (this.layoutEvents) {
        this.emitLayout();
      }
    }
    if (ref instanceof _value.VariableReference) {
      if (ref.type instanceof _value.CollectionItemVariable) {
        this.updateCollectionName(ref, ref.param.collection, undefined);
      } else if (ref.type instanceof _value.CollectionVariable) {
        this.updateCollectionName(ref, ref.param.name, undefined);
      }
    }
  }
  removeVariable(ref, stop = true) {
    let index = this.variables.findIndex(it => it.ref === ref);
    if (index > -1) {
      let variable = this.variables[index];
      this.variables.splice(index, 1);
      this.removeVariableLookups(variable.name, variable.ref);
      if (stop) {
        if (ref instanceof _value.VariableReference || ref instanceof _value.NullReference) {
          ref.trigger();
        }
        ref.stop();
      }
      return variable;
    }
    return null;
  }
  addVariable(ownerKey, name, variable, source) {
    let oldRef;

    // Only notify of changes if the tree is active
    if (this.active && name) {
      oldRef = this.findVariableByName(name, false);
    }
    let info = Object.freeze({
      owner: ownerKey,
      name,
      type: variable.valueType,
      varType: variable.variableType,
      ref: variable,
      source
    });
    if (variable instanceof _value.VariableReference) {
      variable.attach(this.resolver, this);
      this.addCollections(variable, info);
    }
    this.variables.push(info);
    if (name) {
      if (!this.names[name]) {
        this.names[name] = [];
      }
      this.names[name].push(info);
      if (oldRef) {
        if (oldRef instanceof _value.VariableReference) {
          oldRef.trigger();
        } else if (oldRef instanceof _value.NullReference) {
          oldRef.trigger();
          // If this context has a null variable with the same name, remove that now
          let index = this.variables.findIndex(it => it.ref === oldRef);
          if (index > -1) {
            let oldVar = this.variables[index];
            this.variables.splice(index, 1);
            // Remove the null from names if it didn't get overwritten before
            let nameIndex = this.names[oldVar.name].findIndex(it => it.ref === oldRef);
            if (nameIndex > -1) {
              this.names[oldVar.name].splice(nameIndex, 1);
            }
          }
        }
      }
      if (this.layoutEvents) {
        this.emitLayout();
      }
    }
    return variable;
  }
  findVariableByName(name, addNull = true) {
    return this.findVar(c => {
      if (c.names[name] && c.names[name].length > 0) {
        return {
          ref: c.names[name][0].ref
        };
      }
      return undefined;
    }, name, undefined, addNull);
  }
  findVariableByType(type, addNull = true) {
    return this.findVar(c => {
      let ref = c.variables.find(it => it.type === type);
      if (ref) {
        return ref;
      }
      return undefined;
    }, undefined, type, addNull);
  }
  addCollections(variable, info) {
    if (variable.type instanceof _value.CollectionItemVariable) {
      let param = variable.param;
      if (param !== null && param !== void 0 && param.collection) {
        this.hasCollectionItems = true;
        if (!this.collectionItems[param.collection]) {
          this.collectionItems[param.collection] = [info];
        } else {
          this.collectionItems[param.collection].push(info);
        }
        this.triggerCollections(param.collection);
      }
    } else if (variable.type instanceof _value.CollectionVariable) {
      let param = variable.param;
      if (param !== null && param !== void 0 && param.name) {
        this.hasCollections = true;
        if (!this.collections[param.name]) {
          this.collections[param.name] = [info];
        } else {
          this.collections[param.name].push(info);
        }
      }
    }
  }
  updateCollectionName(variable, oldName, name) {
    if (!oldName && !name) {
      return;
    } else if (oldName === name) {
      return;
    }
    let info = this.variables.find(it => it.ref === variable);
    let triggerOld;
    let triggerNew;
    if (variable.type instanceof _value.CollectionItemVariable) {
      if (oldName) {
        var _this$collectionItems, _this$collectionItems2;
        triggerOld = oldName;
        let index = (_this$collectionItems = this.collectionItems) === null || _this$collectionItems === void 0 ? void 0 : (_this$collectionItems2 = _this$collectionItems[oldName]) === null || _this$collectionItems2 === void 0 ? void 0 : _this$collectionItems2.findIndex(it => it.ref === variable);
        if (index > -1) {
          this.collectionItems[oldName].splice(index, 1);
          if (this.collectionItems[oldName].length === 0) {
            delete this.collectionItems[oldName];
          }
        }
      }
      if (name) {
        triggerNew = name;
        variable.updateParameter("collection", name);
        this.hasCollectionItems = true;
        if (!this.collectionItems[name]) {
          this.collectionItems[name] = [info];
        } else {
          this.collectionItems[name].push(info);
        }
      } else {
        variable.updateParameter("collection", undefined);
        if (Object.keys(this.collectionItems).length === 0) {
          this.hasCollectionItems = false;
        }
      }
    } else if (variable.type instanceof _value.CollectionVariable) {
      if (oldName) {
        var _this$collections, _this$collections$old;
        triggerOld = oldName;
        let index = (_this$collections = this.collections) === null || _this$collections === void 0 ? void 0 : (_this$collections$old = _this$collections[oldName]) === null || _this$collections$old === void 0 ? void 0 : _this$collections$old.findIndex(it => it.ref === variable);
        if (index > -1) {
          this.collections[oldName].splice(index, 1);
          if (this.collections[oldName].length === 0) {
            delete this.collections[oldName];
          }
        }
      }
      if (name) {
        triggerNew = name;
        this.hasCollections = true;
        variable.updateParameter("name", name);
        if (!this.collections[name]) {
          this.collections[name] = [info];
        } else {
          this.collections[name].push(info);
        }
      } else {
        variable.updateParameter("name", undefined);
        if (Object.keys(this.collections).length === 0) {
          this.hasCollections = false;
        }
      }
    }
    if (triggerNew) {
      this.triggerCollections(triggerNew);
    }
    if (triggerOld) {
      this.triggerCollections(triggerOld);
    }
  }
  triggerCollections(name) {
    let ctx = this;
    while (ctx) {
      if (ctx.hasCollections && ctx.collections[name]) {
        for (let it of ctx.collections[name]) {
          it.ref.triggerDeps();
        }
      }
      ctx = ctx.parent;
    }
  }
  findCollections() {
    let ctx = this;
    let collections = new Set();
    while (ctx) {
      if (ctx.hasCollections) {
        for (let it of Object.keys(ctx.collections)) {
          collections.add(it);
        }
      }
      ctx = ctx.parent;
    }
    return Array.from(collections);
  }
  findCollectionItems(collection) {
    let layer = [this];
    let collected = [];
    while (layer.length > 0) {
      let nextLayer = [];
      for (let it of layer) {
        if (it.hasCollectionItems && it.collectionItems[collection]) {
          collected = collected.concat(it.collectionItems[collection]);
        }
        nextLayer = nextLayer.concat(it.children);
      }
      layer = nextLayer;
    }
    return collected;
  }
  batch(network) {
    return this.batches.batch(network);
  }
  getNft(contract, tokenId) {
    return this.nft.getToken(this.batches, contract, tokenId);
  }
  getOwnedNfts(contract, account) {
    return this.nft.getItems(this.batches, contract, account);
  }
  get variableUpdates() {
    return this.batches.variableUpdates;
  }
  off(listener) {
    return this.batches.off(listener);
  }
  onLogs(network, filter, listener) {
    return this.batches.onLogs(network, filter, listener);
  }
  knownVariables() {
    if (!this.attached) {
      return [];
    }
    let vars = [];
    let ctx = this;
    while (ctx) {
      for (let it of ctx.variables) {
        if (it.name) {
          vars.push(it);
        }
      }
      ctx = ctx.parent;
    }
    return vars;
  }
  findVar(tester, name, type, addNull = true) {
    if (!this.attached) {
      return null;
    }
    let ctx = this;
    while (ctx) {
      let ret = tester(ctx);
      if (ret) {
        return ret.ref;
      }
      if (ctx.parent) {
        ctx = ctx.parent;
      } else {
        if (addNull && (name || type)) {
          let nullRef = new _value.NullReference();
          let info = Object.freeze({
            name,
            type,
            varType: "null",
            owner: this.ownerKey,
            ref: nullRef
          });
          if (name) {
            if (!ctx.names[name]) {
              ctx.names[name] = [];
            }
            ctx.names[name].push(info);
          } else if (!type) {
            return null;
          }
          this.variables.push(info);
          return nullRef;
        }
        return null;
      }
    }
    return null;
  }
  findContext(name) {
    if (!name) {
      return this;
    }
    let ctx = this;
    while (ctx) {
      if (ctx.name === name) {
        return ctx;
      }
      ctx = ctx.parent;
    }
    return null;
  }
}
exports.ContextManager = ContextManager;
//# sourceMappingURL=ContextManager.js.map